import { customFieldDefinitionsApi } from 'app/services/api/customFieldDefinitionsApi';
import _ from 'lodash';

import { LOAN_APPLICATION_ACTION_NAME_PREFIX } from 'constants/loanApplication';
import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { getUuid, selectUserData } from 'store/auth/selectors';

export const CUSTOM_FIELD_VALUES_ACTION_TYPE = createNamedActions(
  // LOAN_APPLICATION_ACTION_NAME_PREFIX is used for Redux Middleware in Loan Editor
  `${LOAN_APPLICATION_ACTION_NAME_PREFIX}/customFieldValues`,
  {
    SET_CUSTOM_FIELD_VALUES: 'SET_CUSTOM_FIELD_VALUES',
    SET_LOADING: 'SET_LOADING',
  },
);

const setCustomFieldValuesAction = ({ applicationId, values, metadata }) => ({
  type: CUSTOM_FIELD_VALUES_ACTION_TYPE.SET_CUSTOM_FIELD_VALUES,
  payload: { applicationId, values, ...(metadata ? { metadata } : {}) },
});

const setLoadingAction = ({ applicationId, isLoading }) => ({
  type: CUSTOM_FIELD_VALUES_ACTION_TYPE.SET_LOADING,
  payload: { applicationId, isLoading },
});

const buildCurrentUserHistoryInfo = ({ userId, userData }) => {
  if (!userId) {
    return null;
  }

  return {
    id: userId,
    firstName: userData?.firstName ?? null,
    lastName: userData?.lastName ?? null,
    email: userData?.email ?? null,
  };
};

const adaptLoansHistoryEntryToFrontendShape = (entry, currentUserHistoryInfo) => ({
  applicationHistoryEntryId: entry.id,
  applicationId: entry.applicationId,
  tabId: entry.tabId ?? null,
  createdByUser: currentUserHistoryInfo,
  createdByOriginalUser: null,
  appliedByUser: null,
  appliedByOriginalUser: null,
  createdDate: entry.created?.dateTime ?? null,
  rootEntityTypeName: entry.rootEntityTypeName,
  parentProperty: entry.parentProperty ?? null,
  entityTypeName: entry.entityTypeName,
  actionType: entry.actionType,
  entityPrimaryKey: entry.entityPrimaryKey,
  historyState: entry.historyState,
  properties: (entry.properties ?? []).map(property => ({
    applicationHistoryPropertyId: property.id,
    applicationHistoryEntryId: entry.id,
    propertyName: property.propertyName,
    oldValue: property.oldValue ?? null,
    newValue: property.newValue ?? null,
    oldValueDetails: null,
    newValueDetails: null,
    type: property.type,
    baseType: property.baseType,
  })),
});

export const loadCustomFieldValuesAction = catchAsyncActionError(
  ({ applicationId }) =>
    async dispatch => {
      dispatch(setLoadingAction({ applicationId, isLoading: true }));

      try {
        const response =
          await customFieldDefinitionsApi.getApplicationCustomFieldValues(applicationId);
        const values = Array.isArray(response.data) ? response.data : [];

        dispatch(setCustomFieldValuesAction({ applicationId, values }));

        return values;
      } finally {
        dispatch(setLoadingAction({ applicationId, isLoading: false }));
      }
    },
);

export const saveManualCustomFieldValuesAction = catchAsyncActionError(
  ({ applicationId, values }) =>
    async (dispatch, getState) => {
      const saveResponse = await customFieldDefinitionsApi.saveManualCustomFieldValues(
        applicationId,
        values,
      );
      const rawHistoryIncrement = Array.isArray(saveResponse?.data) ? saveResponse.data : [];

      const refreshedResponse =
        await customFieldDefinitionsApi.getApplicationCustomFieldValues(applicationId);
      const refreshedValues = Array.isArray(refreshedResponse.data) ? refreshedResponse.data : [];

      const state = getState();
      const currentUserHistoryInfo = buildCurrentUserHistoryInfo({
        userId: getUuid(state),
        userData: selectUserData(state),
      });
      const historyIncrement = rawHistoryIncrement.map(entry =>
        adaptLoansHistoryEntryToFrontendShape(entry, currentUserHistoryInfo),
      );
      const metadata = _.isEmpty(historyIncrement) ? null : { historyIncrement };

      dispatch(setCustomFieldValuesAction({ applicationId, values: refreshedValues, metadata }));

      return refreshedValues;
    },
);
